"use client"

import { ampli } from "@/ampli"
import { IconButton, Spinner } from "@/elements"
import type { NovaSession } from "@/types"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { FormProvider } from "react-hook-form"
import { Dialog, DialogContent } from "../Dialog"
import { BottomNav } from "./BottomNav"
import { InviteCreativesStep } from "./InviteCreativesStep"
import { CreativeInvite, SuccessStep } from "./SuccessStep"
import { StepOne } from "./StepOne"
import { StepThree } from "./StepThree"
import { StepTwo } from "./StepTwo"
import { PartialPostAJobModalFormValues } from "./types"
import { usePostAJobModal } from "./usePostAJobModal"
import { decodeGraphqlNodeId } from "@/tracking"
import { useState } from "react"
import { PostAJobResponseDataFragment } from "@/gql/graphql"
import clsx from "clsx"

interface Props {
  onClose: () => void
  open: boolean
  serverSession: NovaSession
  loading?: boolean
  isEditingPostedJob?: boolean
  updateData?: PartialPostAJobModalFormValues | null
  entryPoint: "find-work" | "hiring" | "draft-jobs" | "job-management"
}

interface ContentProps {
  step: number
  jobPostId: string | undefined
  wrapperRef: React.RefObject<HTMLDivElement | null>
  serverSession: NovaSession
  stepOneDisplayFields: { position: string; location: string; workType: string }
  onChangeStepOneDisplayField: (fieldName: string, value: string) => void
  jobPostingResponseData: PostAJobResponseDataFragment | null
  selectedCreativeIdsToInvite: string[]
  handleClickInviteCard: (creativeId: string) => void
  invitedCreatives: CreativeInvite[]
}

const Content: React.FC<ContentProps> = ({
  step,
  jobPostId,
  jobPostingResponseData,
  wrapperRef,
  serverSession,
  stepOneDisplayFields,
  onChangeStepOneDisplayField,
  selectedCreativeIdsToInvite,
  handleClickInviteCard,
  invitedCreatives,
}) => {
  switch (step) {
    case 2:
      return <StepTwo serverSession={serverSession} jobPostId={jobPostId || ""} />
    case 3:
      return <StepThree />
    case 4:
      return (
        <InviteCreativesStep
          jobPostingResponseData={jobPostingResponseData}
          serverSession={serverSession}
          handleClickInviteCard={handleClickInviteCard}
          selectedCreatives={selectedCreativeIdsToInvite}
        />
      )
    case 5:
      return (
        <SuccessStep
          jobPostingResponseData={jobPostingResponseData}
          wrapperRef={wrapperRef}
          invitedCreatives={invitedCreatives}
        />
      )
    case 1:
    default:
      return (
        <StepOne
          jobPostId={jobPostId || ""}
          stepOneDisplayFields={stepOneDisplayFields}
          onChangeStepOneDisplayField={onChangeStepOneDisplayField}
        />
      )
  }
}

export const PostAJobModal: React.FC<Props> = ({
  onClose,
  open,
  serverSession,
  isEditingPostedJob = false,
  loading = false,
  updateData,
  entryPoint,
}) => {
  const {
    step,
    isSubmitting,
    isCreatingDraft,
    onNext,
    onBack,
    onDone,
    createAndDraftJobPost,
    createAndPublishJobPost,
    updatePublishedJobPost,
    wrapperRef,
    methods,
    handleReset,
    stepOneDisplayFields,
    onChangeStepOneDisplayField,
    responseData,
    handleInviteCreatives,
    selectedCreativeIdsToInvite,
    handleClickInviteCard,
    isMutatingInviteCreatives,
    invitedCreatives,
  } = usePostAJobModal(serverSession, onClose, updateData)

  const jobPostingId = updateData?.jobPostingId || responseData?.id

  const onOpenChange = (open: boolean) => {
    if (!open) {
      if (!isEditingPostedJob) {
        if (step > 3) {
          ampli.postAJobExit({
            job_id: decodeGraphqlNodeId(jobPostingId).numericalId || "",
          })
        }
        if (step === 4) {
          ampli.inviteToJobExit({
            entry_point: "JobPostingForm",
            job_id: decodeGraphqlNodeId(jobPostingId).numericalId || "",
          })
        }
      }

      handleReset()
      onClose()
    } else {
      if (!isEditingPostedJob) {
        ampli.postAJobStart({
          job_id: decodeGraphqlNodeId(jobPostingId).numericalId || "",
          entry_point: entryPoint,
        })
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent withClose={false} className="!max-w-fit overflow-y-auto">
        <FormProvider {...methods}>
          <div
            className={clsx(
              "flex h-full max-h-screen w-screen flex-col overflow-x-hidden overflow-y-scroll sm:max-modal-size",
              step === 4 ? "lg:w-[868px]" : "md:w-[540px]"
            )}>
            <div className="flex flex-1 flex-col overflow-y-scroll" ref={wrapperRef}>
              <div className="absolute right-4 top-5 z-50 sm:right-6 sm:top-6">
                <IconButton
                  onClick={() => {
                    if (!isEditingPostedJob) {
                      ampli.postAJobExit({
                        job_id: decodeGraphqlNodeId(jobPostingId).numericalId || "",
                      })
                    }
                    onClose()
                    onDone()
                  }}
                  Icon={XMarkIcon}
                  variant="white"
                />
              </div>
              {loading ? (
                <div className="flex h-[584px] flex-col items-center justify-center">
                  <Spinner />
                </div>
              ) : (
                <div className="px-4 sm:px-8 md:px-10">
                  <Content
                    invitedCreatives={invitedCreatives}
                    jobPostId={jobPostingId}
                    step={step}
                    jobPostingResponseData={responseData}
                    wrapperRef={wrapperRef}
                    serverSession={serverSession}
                    stepOneDisplayFields={stepOneDisplayFields}
                    onChangeStepOneDisplayField={onChangeStepOneDisplayField}
                    selectedCreativeIdsToInvite={selectedCreativeIdsToInvite}
                    handleClickInviteCard={handleClickInviteCard}
                  />
                </div>
              )}
            </div>
            <BottomNav
              isEditingPostedJob={isEditingPostedJob}
              loading={loading}
              step={step}
              onBack={onBack}
              onNext={onNext}
              onDone={() => {
                onClose()
                onDone()
              }}
              isMutatingInviteCreatives={isMutatingInviteCreatives}
              handleInviteCreatives={handleInviteCreatives}
              isCreatingDraft={isCreatingDraft}
              createAndPublishJobPost={methods.handleSubmit(createAndPublishJobPost)}
              createAndDraftJobPost={methods.handleSubmit(createAndDraftJobPost)}
              updatePostedJob={methods.handleSubmit(updatePublishedJobPost)}
              isSubmitting={isSubmitting}
              selectedCreativeIdsToInvite={selectedCreativeIdsToInvite}
              jobPostingId={jobPostingId}
            />
          </div>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
